/**
 * One repricing pass as a filter of the Activity log (CU-869f03vek).
 *
 * The pass is `scope.execution_id` on every event and `execution_id` on
 * the wire — the name the webback has accepted since the Executions
 * screen linked here. What is new is where the filter can be set from:
 * the pass shown on a row and in its detail, and the search box when
 * the whole text is a pass id. This module is the shape and the state
 * patch; the page draws the chips.
 *
 * No React, so it runs under `node --test`.
 */

//: The id the sweep mints: `rp-<slot YYYYMMDDHHMM>-<pass label>-<6 hex>`
//: (pms-adapter `src/repricing/sweep/runner.py`, `execution_id = f"rp-…"`).
//: Strict on purpose: `rp-2026` on its way to being typed is a search
//: for the text, not a pass filter.
const PASS_ID = /^rp-\d{12}-[a-z0-9_]+-[0-9a-f]{6}$/i

/** Does this text read as a whole pass id? */
export function isPassId(text) {
	return PASS_ID.test(String(text ?? '').trim())
}

/** The pass id the search box holds — the whole text, trimmed, lower-case — or `null`. */
export function passFromSearch(text) {
	const t = String(text ?? '').trim()
	return isPassId(t) ? t.toLowerCase() : null
}

/** `Pass 202610280707…`: the slot without the prefix — the band's chip
 *  read so, minus the dash it used to drag along. The full id is the title. */
export function passLabel(id) {
	return `Pass ${String(id ?? '').replace(/^rp-/i, '').slice(0, 12)}…`
}

/**
 * The filters with one pass chosen. The dates open up — the pass may be
 * older than the window, same as when the pass arrives by link from
 * Executions — and a search that was the pass itself is spent; any
 * other search, and every other filter, stays.
 */
export function withPass(filters, id) {
	const f = filters || {}
	const spent = passFromSearch(f.search) === String(id).toLowerCase()
	return { ...f, execution_id: id, from: '', to: '', search: spent ? '' : (f.search ?? '') }
}
